import React, { useState, useEffect } from 'react';
import { ElementoValoracion } from '../../types';
import { FiX, FiCheck } from 'react-icons/fi';
import './ValoracionFase.css';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: (item: ElementoValoracion) => void;
    initialData?: ElementoValoracion | null;
}

const TIPO_OPCIONES = [
    'Continente',
    'Contenido',
    'Responsabilidad Civil',
    'Daños Estéticos',
    'Reusado',
    'Otro (Personalizado)'
];

const ElementoValoracionModal: React.FC<Props> = ({ isOpen, onClose, onSave, initialData }) => {
    const [tipoSeleccionado, setTipoSeleccionado] = useState<string>('Continente');
    const [tipoLibre, setTipoLibre] = useState<string>('');
    const [perjudicado, setPerjudicado] = useState<'Asegurado' | 'Tercero'>('Asegurado');
    const [nombreTercero, setNombreTercero] = useState<string>('');
    const [ia, setIa] = useState<'Indemnizar' | 'Asistencia'>('Indemnizar');
    const [concepto, setConcepto] = useState<string>('');
    const [unidades, setUnidades] = useState<number>(1);
    const [precioUnitario, setPrecioUnitario] = useState<number>(0);
    const [depreciacion, setDepreciacion] = useState<number>(0);

    useEffect(() => {
        if (initialData) {
            const isStandard = TIPO_OPCIONES.includes(initialData.tipo);
            if (isStandard && initialData.tipo !== 'Otro (Personalizado)') {
                setTipoSeleccionado(initialData.tipo);
                setTipoLibre('');
            } else {
                setTipoSeleccionado('Otro (Personalizado)');
                setTipoLibre(initialData.tipo || '');
            }
            setPerjudicado((initialData.perjudicado as any) || 'Asegurado');
            setNombreTercero(initialData.nombre_tercero || '');
            setIa((initialData.ia as any) || 'Indemnizar');
            setConcepto(initialData.concepto || '');
            setUnidades(initialData.unidades ?? 1);
            setPrecioUnitario(initialData.precio_unitario ?? 0);
            setDepreciacion(initialData.depreciacion ?? 0);
        } else {
            setTipoSeleccionado('Continente');
            setTipoLibre('');
            setPerjudicado('Asegurado');
            setNombreTercero('');
            setIa('Indemnizar');
            setConcepto('');
            setUnidades(1);
            setPrecioUnitario(0);
            setDepreciacion(0);
        }
    }, [initialData, isOpen]);

    if (!isOpen) return null;

    const precioTotal = Number((unidades * precioUnitario).toFixed(2));
    const valorReal = Number((precioTotal * (1 - (depreciacion / 100))).toFixed(2));

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        const tipoFinal = tipoSeleccionado === 'Otro (Personalizado)' 
            ? (tipoLibre.trim() || 'Personalizado') 
            : tipoSeleccionado;

        const newItem: ElementoValoracion = {
            id: initialData?.id || `item_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
            tipo: tipoFinal,
            perjudicado,
            nombre_tercero: perjudicado === 'Tercero' ? (nombreTercero.trim() || undefined) : undefined,
            ia,
            concepto: concepto.trim(),
            unidades: Number(unidades) || 0,
            precio_unitario: Number(precioUnitario) || 0,
            precio_total: precioTotal,
            depreciacion: Number(depreciacion) || 0,
            valor_real: valorReal
        };

        onSave(newItem);
        onClose();
    };

    return (
        <div className="val-modal-overlay">
            <div className="val-modal-card">
                <div className="val-modal-header">
                    <h3 className="val-modal-title">
                        {initialData ? 'Editar Elemento de Valoración' : 'Añadir Elemento de Valoración'}
                    </h3>
                    <button 
                        type="button"
                        onClick={onClose}
                        className="val-modal-close"
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <form onSubmit={handleFormSubmit}>
                    <div className="val-modal-body">
                        {/* Tipo */}
                        <div className="val-form-group">
                            <label>Tipo</label>
                            <select 
                                value={tipoSeleccionado} 
                                onChange={(e) => setTipoSeleccionado(e.target.value)}
                                className="val-form-control"
                            >
                                {TIPO_OPCIONES.map(op => (
                                    <option key={op} value={op}>{op}</option>
                                ))}
                            </select>
                            {tipoSeleccionado === 'Otro (Personalizado)' && (
                                <input 
                                    type="text"
                                    placeholder="Escribe el tipo personalizado..."
                                    value={tipoLibre}
                                    onChange={(e) => setTipoLibre(e.target.value)}
                                    className="val-form-control"
                                    style={{ marginTop: '6px' }}
                                    required
                                />
                            )}
                        </div>

                        <div className="val-grid-2">
                            {/* Perjudicado */}
                            <div className="val-form-group">
                                <label>Perjudicado</label>
                                <select 
                                    value={perjudicado}
                                    onChange={(e) => setPerjudicado(e.target.value as any)}
                                    className="val-form-control"
                                >
                                    <option value="Asegurado">Asegurado</option>
                                    <option value="Tercero">Tercero</option>
                                </select>
                            </div>

                            {/* I/A */}
                            <div className="val-form-group">
                                <label>I/A (Indemnizar / Asistencia)</label>
                                <select 
                                    value={ia}
                                    onChange={(e) => setIa(e.target.value as any)}
                                    className="val-form-control"
                                >
                                    <option value="Indemnizar">Indemnizar</option>
                                    <option value="Asistencia">Asistencia</option>
                                </select>
                            </div>
                        </div>

                        {perjudicado === 'Tercero' && (
                            <div className="val-form-group">
                                <label>Nombre del Tercero</label>
                                <input 
                                    type="text"
                                    placeholder="Nombre completo de la persona perjudicada (Tercero)..."
                                    value={nombreTercero}
                                    onChange={(e) => setNombreTercero(e.target.value)}
                                    className="val-form-control"
                                    required
                                />
                            </div>
                        )}

                        {/* Concepto */}
                        <div className="val-form-group">
                            <label>Concepto</label>
                            <textarea 
                                rows={2}
                                placeholder="Descripción del concepto..."
                                value={concepto}
                                onChange={(e) => setConcepto(e.target.value)}
                                className="val-form-control"
                                required
                            />
                        </div>

                        <div className="val-grid-3">
                            {/* Unidades */}
                            <div className="val-form-group">
                                <label>Unidades</label>
                                <input 
                                    type="number"
                                    step="any"
                                    min="0"
                                    value={unidades}
                                    onChange={(e) => setUnidades(parseFloat(e.target.value) || 0)}
                                    className="val-form-control"
                                    required
                                />
                            </div>

                            {/* Precio Unitario */}
                            <div className="val-form-group">
                                <label>Precio Unitario (€)</label>
                                <input 
                                    type="number"
                                    step="any"
                                    min="0"
                                    value={precioUnitario}
                                    onChange={(e) => setPrecioUnitario(parseFloat(e.target.value) || 0)}
                                    className="val-form-control"
                                    required
                                />
                            </div>

                            {/* Depreciación */}
                            <div className="val-form-group">
                                <label>Depreciación (%)</label>
                                <input 
                                    type="number"
                                    step="any"
                                    min="0"
                                    max="100"
                                    value={depreciacion}
                                    onChange={(e) => setDepreciacion(parseFloat(e.target.value) || 0)}
                                    className="val-form-control"
                                    required
                                />
                            </div>
                        </div>

                        {/* Calculated Previews */}
                        <div className="val-summary-box">
                            <div className="val-summary-item">
                                <span className="val-summary-label">Precio Total</span>
                                <span className="val-summary-val">
                                    {precioTotal.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                </span>
                            </div>
                            <div className="val-summary-item">
                                <span className="val-summary-label">Valor Real</span>
                                <span className="val-summary-val highlight">
                                    {valorReal.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="val-modal-footer">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                        >
                            <FiCheck style={{ marginRight: '4px' }} />
                            Guardar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ElementoValoracionModal;
